const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? 'https://flupcarry-api.niller.workers.dev' : '')

export const getWsUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? 'https://flupcarry-api.niller.workers.dev' : '')
  if (apiUrl) {
    return apiUrl.replace(/^http/, 'ws') + '/ws'
  }
  return typeof window !== 'undefined'
    ? `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`
    : 'ws://localhost:3000/ws'
}

// Auto-Execute Worker types
export interface AutoExecuteWorkerStatus {
  success: boolean
  running: boolean
  stats: {
    running: boolean
    startedAt: string | null
    scansCompleted: number
    hedgesFound: number
    entriesFound: number
    tradesExecuted: number
    tradesSkipped: number
    totalInvested: number
    lastScanAt: string | null
    lastTradeAt: string | null
    errors: number
    lastError: string | null
  }
  config: {
    enabled: boolean
    scanInterval: number
    cryptos: string[]
    intervals: string[]
    maxPositions: number
    maxInvestmentPerTrade: number
    maxTotalInvestment: number
  }
}

export interface AutoExecuteScanEntry {
  id: number
  timestamp: string
  marketsScanned: number
  hedgesFound: number
  entriesFound: number
  tradesExecuted: number
  tradesSkipped: number
  skipReasons: Record<string, number>
  invested: number
  duration: number
  status: 'success' | 'error'
  error?: string
}

export interface MarketScanDetail {
  slug?: string
  crypto: string
  interval?: string
  strikePrice: number
  oraclePrice: number
  askPrice: number
  edge: number
  favoriteThreshold?: number
  timeRemaining?: number
  endDateMs?: number
  isHedge?: boolean
  isEntry?: boolean
  reason?: string
}

// Database types
export interface DbStats {
  success: boolean
  worker: {
    id: number
    scanCount: number
    opportunitiesFound: number
    ordersPlaced: number
    ordersFilled: number
    ordersCancelled: number
    totalProfit: number
    totalLoss: number
    startTime: number
    lastScanTime: number
  }
  database: {
    totalOrders: number
    totalTrades: number
    totalScans: number
    dbSizeKB: number
  }
  performance: {
    totalTrades: number
    winningTrades: number
    losingTrades: number
    totalProfit: number
    totalLoss: number
    netProfit: number
    winRate: number
    avgProfit: number
    avgLoss: number
  }
}

export interface Order {
  id: number
  orderId: string
  tokenId: string
  conditionId: string
  outcome: string
  price: number
  size: number
  side: string
  status: string
  cryptoSymbol?: string
  strategyType?: string
  createdAt: string
  updatedAt: string
  filledAt?: string
}

export interface Trade {
  id: number
  tradeId: string
  orderId: string
  tokenId: string
  outcome: string
  entryPrice: number
  exitPrice?: number
  size: number
  profitLoss?: number
  status: string
  cryptoSymbol?: string
  enteredAt: string
  exitedAt?: string
}

export interface ScanResult {
  id: number
  tokenId: string
  outcome: string
  price: number
  type: string
  cryptoSymbol: string
  scannedAt: string
  orderId?: string
}

// Auto-Execute Worker API Functions
export async function fetchAutoExecuteStatus(): Promise<AutoExecuteWorkerStatus> {
  const response = await fetch(`${API_BASE_URL}/auto-execute/status`)
  if (!response.ok) throw new Error('Failed to fetch auto-execute status')
  return response.json()
}

export async function startAutoExecute(): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/auto-execute/start`, { method: 'POST' })
  if (!response.ok) throw new Error('Failed to start auto-execute')
  return response.json()
}

export async function stopAutoExecute(): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/auto-execute/stop`, { method: 'POST' })
  if (!response.ok) throw new Error('Failed to stop auto-execute')
  return response.json()
}

export async function fetchAutoExecuteScanHistory(limit?: number): Promise<{ success: boolean; history: AutoExecuteScanEntry[]; count: number }> {
  const query = limit ? `?limit=${limit}` : ''
  const response = await fetch(`${API_BASE_URL}/auto-execute/scan-history${query}`)
  if (!response.ok) throw new Error('Failed to fetch auto-execute scan history')
  return response.json()
}

export async function fetchLastScan(): Promise<MarketScanDetail[]> {
  const response = await fetch(`${API_BASE_URL}/auto-execute/last-scan`)
  const data = await response.json()
  if (!data.success) throw new Error(data.error)
  return data.data
}

export async function fetchDbStats(): Promise<DbStats> {
  const response = await fetch(`${API_BASE_URL}/db/stats`)
  if (!response.ok) throw new Error('Failed to fetch database stats')
  return response.json()
}

export async function fetchOrders(params?: { limit?: number; status?: string }): Promise<Order[]> {
  const query = new URLSearchParams()
  if (params?.limit) query.append('limit', params.limit.toString())
  if (params?.status) query.append('status', params.status)
  
  const response = await fetch(`${API_BASE_URL}/db/orders?${query}`)
  if (!response.ok) throw new Error('Failed to fetch orders')
  return response.json()
}

export async function fetchOpenOrders(): Promise<Order[]> {
  const response = await fetch(`${API_BASE_URL}/db/orders/open`)
  if (!response.ok) throw new Error('Failed to fetch open orders')
  return response.json()
}

export async function fetchTrades(params?: { limit?: number }): Promise<{ trades: Trade[]; metrics: unknown }> {
  const query = new URLSearchParams()
  if (params?.limit) query.append('limit', params.limit.toString())
  
  const response = await fetch(`${API_BASE_URL}/db/trades?${query}`)
  if (!response.ok) throw new Error('Failed to fetch trades')
  return response.json()
}

export async function fetchScans(params?: { limit?: number }): Promise<ScanResult[]> {
  const query = new URLSearchParams()
  if (params?.limit) query.append('limit', params.limit.toString())
  
  const response = await fetch(`${API_BASE_URL}/db/scans?${query}`)
  if (!response.ok) throw new Error('Failed to fetch scans')
  return response.json()
}

export async function scanOpportunities(): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}/strategy/scan`)
  if (!response.ok) throw new Error('Failed to scan opportunities')
  return response.json()
}

// Carry Trade Types
export interface CarryTradePosition {
  id: string
  marketSlug: string
  crypto: string
  conditionId: string
  endDate: string
  longshot?: {
    side: 'Up' | 'Down'
    tokenId: string
    entryPrice: number
    shares: number
    entryTime: string
    currentPrice?: number
    orderId?: string
  }
  favorite?: {
    side: 'Up' | 'Down'
    tokenId: string
    entryPrice: number
    shares: number
    entryTime: string
    currentPrice?: number
    orderId?: string
  }
  hedge?: {
    side: 'Up' | 'Down'
    tokenId: string
    entryPrice: number
    shares: number
    entryTime: string
    currentPrice?: number
    orderId?: string
  }
  entry?: {
    side: 'Up' | 'Down'
    tokenId: string
    entryPrice: number
    shares: number
    entryTime: string
    currentPrice?: number
    orderId?: string
  }
  status: 'MERGED' | 'FAILED' | 'WAITING_ENTRY' | 'HAS_ENTRY' | 'HAS_HEDGE' | 'HEDGED' | 'PUMPED' | 'CLOSED'
  closedAt?: string
  totalInvested: number
  totalCost?: number
  mergeTxHash?: string
  arbSpread?: number
  currentValue: number
  unrealizedPnL: number
  realizedPnL?: number
}

export interface CarryTradeStats {
  total: number
  byStatus: Record<string, number>
  totalInvested: number
  totalCurrentValue: number
  totalUnrealizedPnL: number
  totalRealizedPnL: number
  avgReturnPct: number
}

export interface CarryTradePositionsResponse {
  success: boolean
  positions: CarryTradePosition[]
  stats: CarryTradeStats
}

// Carry Trade API Functions
export async function scanCarryOpportunities(cryptos?: string[]): Promise<unknown> {
  const query = cryptos ? `?cryptos=${cryptos.join(',')}` : ''
  const response = await fetch(`${API_BASE_URL}/carry/scan${query}`)
  if (!response.ok) throw new Error('Failed to scan carry trade opportunities')
  return response.json()
}

export async function executeCarryTrade(params: {
  cryptos?: string[]
  shareAmount?: number
}): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}/carry/execute`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params)
  })
  if (!response.ok) throw new Error('Failed to execute carry trade')
  return response.json()
}

export async function fetchCarryPositions(): Promise<CarryTradePositionsResponse> {
  const response = await fetch(`${API_BASE_URL}/carry/positions`)
  if (!response.ok) throw new Error('Failed to fetch carry trade positions')
  return response.json()
}

export async function clearCarryPositions(): Promise<unknown> {
  const response = await fetch(`${API_BASE_URL}/carry/clear-positions`, {
    method: 'POST'
  })
  if (!response.ok) throw new Error('Failed to clear carry trade positions')
  return response.json()
}

// P&L Analytics Types
export interface PnLAnalytics {
  overall: {
    totalTrades: number
    activeTrades: number
    wins: number
    losses: number
    winRate: number
    totalPnL: number
    totalInvested: number
    avgPnL: number
    roi: number
    activeInvested: number
    activeUnrealizedPnL: number
  }
  bestTrade: {
    crypto: string
    pnl: number
    invested: number
    roi: number
    date: string
  } | null
  worstTrade: {
    crypto: string
    pnl: number
    invested: number
    roi: number
    date: string
  } | null
  byCrypto: Record<string, {
    count: number
    invested: number
    pnl: number
    wins: number
    losses: number
  }>
}

// P&L Analytics API Function
export async function fetchAnalytics(): Promise<{ success: boolean; analytics: PnLAnalytics }> {
  const response = await fetch(`${API_BASE_URL}/carry/analytics`)
  if (!response.ok) throw new Error('Failed to fetch analytics')
  return response.json()
}

// Config Management Types
export interface BotConfig {
  trading: {
    cryptos: string[]
    intervals: string[]
    maxPositions: number
    maxInvestmentPerTrade: number
    maxTotalInvestment: number
  }
  carry: {
    longshotThreshold: number
    favoriteThreshold: number
    longshotPumpThreshold: number
    earlyPhaseMinutes: number
    latePhaseMinutes: number
    executionModel: 'model-a' | 'model-b'
    entryBufferSeconds: number
    maxBidAskSpread: number
    minOrderbookDepth: number
    orderbookDepthBand: number
    minMarketLiquidity: number
    maxVolatilityPercent: number
    confirmationScans: number
    maxPositionsPerCrypto: number
    sizingAtThreeMinutes: number
    sizingAtOneMinute: number
    sizingUnderOneMinute: number
    useBinanceIndicators: boolean
    binanceEmaFastPeriod: number
    binanceEmaSlowPeriod: number
    binanceRsiPeriod: number
    binanceRsiBullish: number
    binanceRsiBearish: number
    binanceCandleLimit: number
    binanceVwapPeriod: number
    usePostOnly: boolean
    useDynamicSizing: boolean
    enableLongshots: boolean
  }
  worker: {
    scanInterval: number
    enabled: boolean
  }
}

// Config Management API Functions
export async function fetchConfig(): Promise<{ success: boolean; config: BotConfig }> {
  const response = await fetch(`${API_BASE_URL}/config`)
  if (!response.ok) throw new Error('Failed to fetch config')
  return response.json()
}

export async function updateConfig(config: Partial<BotConfig>): Promise<{ success: boolean; message: string; config: BotConfig }> {
  const response = await fetch(`${API_BASE_URL}/config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config)
  })
  if (!response.ok) throw new Error('Failed to update config')
  return response.json()
}

// Orderbook Types
export interface OrderbookLevel {
  price: number
  size: number
}

export interface OrderbookData {
  tokenId: string
  timestamp: number
  bestBid: number
  bestAsk: number
  spread: number
  spreadPercent: number
  bidDepth: OrderbookLevel[]
  askDepth: OrderbookLevel[]
  totalBids: number
  totalAsks: number
}

// Orderbook API Function
export async function fetchOrderbook(tokenId: string): Promise<{ success: boolean; orderbook: OrderbookData }> {
  const response = await fetch(`${API_BASE_URL}/carry/orderbook/${tokenId}`)
  if (!response.ok) throw new Error('Failed to fetch orderbook')
  return response.json()
}

export async function fetchDashboardSession(): Promise<{ success: boolean; authenticated: boolean; disabled?: boolean }> {
  const response = await fetch(`${API_BASE_URL}/dashboard/session`, {
    credentials: 'include'
  })

  if (!response.ok) {
    return { success: false, authenticated: false }
  }

  return response.json()
}

export async function loginDashboard(params: { username: string; password: string }): Promise<{ success: boolean; authenticated?: boolean; error?: string }> {
  const response = await fetch(`${API_BASE_URL}/dashboard/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(params)
  })

  if (!response.ok) {
    const data = await response.json().catch(() => null)
    return { success: false, error: data?.error || 'Login failed' }
  }

  return response.json()
}

export async function logoutDashboard(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE_URL}/dashboard/logout`, {
    method: 'POST',
    credentials: 'include'
  })

  if (!response.ok) {
    throw new Error('Failed to logout')
  }

  return response.json()
}

// P&L History Types
export interface PnLHistoryEntry {
  date: string
  timestamp: number
  totalPnL: number
  cumulativePnL: number
  count: number
  wins: number
  losses: number
  invested: number
  avgPnL: number
  winRate: number
}

export interface RecentActivityEntry {
  id: string
  crypto: string
  status: string
  invested: number
  pnl: number
  date: string
}

// P&L History API Function
export async function fetchPnLHistory(): Promise<{ 
  success: boolean
  history: PnLHistoryEntry[]
  recentActivity: RecentActivityEntry[]
}> {
  const response = await fetch(`${API_BASE_URL}/carry/pnl-history`)
  if (!response.ok) throw new Error('Failed to fetch P&L history')
  return response.json()
}

export interface RedemptionWorkerStats {
  running: boolean
  startedAt: string | null
  scansCompleted: number
  positionsRedeemed: number
  totalAmountReceived: number
  lastScanAt: string | null
  lastRedemptionAt: string | null
  errors: number
  lastError: string | null
}

export async function fetchRedemptionStats(): Promise<{ success: boolean; stats: RedemptionWorkerStats }> {
  const response = await fetch(`${API_BASE_URL}/redemption/stats`)
  if (!response.ok) throw new Error('Failed to fetch redemption stats')
  return response.json()
}

export async function startRedemptionWorker(): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/redemption/start`, { method: 'POST' })
  if (!response.ok) throw new Error('Failed to start redemption worker')
  return response.json()
}

export async function stopRedemptionWorker(): Promise<{ success: boolean; message: string }> {
  const response = await fetch(`${API_BASE_URL}/redemption/stop`, { method: 'POST' })
  if (!response.ok) throw new Error('Failed to stop redemption worker')
  return response.json()
}
